import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';import './App.css';
import TopRibbon from './components/TopRibbon';
import Header from './components/Header';
import Hero from './components/Hero';
import MeditationLibrary from './components/MeditationLibrary';
import Feedback from './components/Feedback';
import CreatorVision from './components/CreatorVision';
import BoxBreathing from './components/BoxBreathing';
import BlogGuides from './components/BlogGuides';
import ShareSection from './components/ShareSection';
import PlayerModal from './components/PlayerModal';
import AdminModal from './components/AdminModal';
import Footer from './components/Footer';
import BlogArchive from './pages/BlogArchive';
import api from './api/client';

function HomePage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);
  const [player, setPlayer] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setError('');
      const [s, f] = await Promise.all([api.listSessions(), api.listFeedback()]);
      setSessions(s);
      setFeedbackList(f);
    } catch (e) {
      setError('Unable to reach sanctuary right now. Please retry in a moment.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Deep link: /?session=<id> auto-opens the player when data is ready
  useEffect(() => {
    if (!sessions.length) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const sid = params.get('session');
      if (sid) {
        const found = sessions.find((s) => s.id === sid);
        if (found) setPlayer(found);
      }
    } catch (_e) { /* ignore */ }
  }, [sessions]);

  const meditations = useMemo(() => sessions.filter(s => s.type === 'meditation'), [sessions]);
  const affirmations = useMemo(() => sessions.filter(s => s.type === 'affirmation'), [sessions]);
  const featured = useMemo(() => sessions.find(s => s.featured) || sessions[0], [sessions]);

  const scrollToBreath = () => {
    const el = document.getElementById('box-breathing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const openPlayer = async (s) => {
    setPlayer(s);
    try {
      const updated = await api.playSession(s.id);
      setSessions((list) => list.map(x => x.id === updated.id ? updated : x));
    } catch (_e) { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-white text-emerald-950 font-sans">
      <TopRibbon onShareClick={() => setShowShare(true)} onBreathClick={scrollToBreath} />
      <Header onAdminClick={() => setShowAdmin(true)} />
      <main>
        {loading ? (
          <div className="max-w-7xl mx-auto px-4 py-24 text-center text-emerald-700">Loading peaceful sanctuary…</div>
        ) : error ? (
          <div className="max-w-7xl mx-auto px-4 py-24 text-center">
            <div className="text-emerald-900">{error}</div>
            <button onClick={() => { setLoading(true); load(); }} className="mt-4 px-4 py-2 rounded-full bg-emerald-700 text-white hover:bg-emerald-800">Retry</button>
          </div>
        ) : (
          <>
            <Hero featured={featured} onPlayFeatured={() => featured && openPlayer(featured)} onStartBreath={scrollToBreath} />
            <MeditationLibrary meditations={meditations} affirmations={affirmations} onPlay={openPlayer} />
            <Feedback feedbackList={feedbackList} setFeedbackList={setFeedbackList} />
            <CreatorVision totalSessions={sessions.length} totalFeedback={feedbackList.length} />
            <BoxBreathing />
            <BlogGuides />
            <ShareSection />
          </>
        )}
      </main>
      <Footer />

      {player && <PlayerModal session={player} onClose={() => setPlayer(null)} />}
      {showAdmin && <AdminModal onClose={() => setShowAdmin(false)} sessions={sessions} onSessionsChange={setSessions} />}
      {showShare && <ShareSection modal onClose={() => setShowShare(false)} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/blogs" element={<BlogArchive />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
